'use client'

import { useTranslations } from 'next-intl'
import { HomeCardImage } from './HomeCardImage'
import { LocaleLink } from '@/components/LocaleLink'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Card } from '@/components/ui/card'
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'

import { externalArticleLink } from './common'
import { HOME_BI_NEWS_TITLE_LINK_ON_DARK } from './home-bi-layout'
import { SeloDestaque } from './SeloDestaque'
import type { BiMobileCarouselSlide } from './types'

/** Largura dos bullets: ativo largo, inativo pequeno. */
const ACTIVE_BULLET = 'h-2 w-8'
const INACTIVE_BULLET = 'h-2 w-2'

const AUTOPLAY_MS = 6000
/** Snap suave ao soltar o arraste (ms). */
const SCROLL_DURATION = 22

/** Respeita prefers-reduced-motion (desliga autoplay/animações). */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])
  return reduced
}

type Props = {
  slides: BiMobileCarouselSlide[]
  /** Até este breakpoint mostra carrossel; acima mostra o grid desktop. */
  visibleBelow?: 'md' | 'lg'
}

export function HomeEditorialMobileCarousel({ slides, visibleBelow = 'md' }: Props) {
  const t = useTranslations('Home')
  const prefersReducedMotion = usePrefersReducedMotion()
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [progressEpoch, setProgressEpoch] = useState(0)
  const [fillRatio, setFillRatio] = useState(0)

  const slideCount = slides.length
  const enableLoop = slideCount > 1
  const autoplayEnabled = enableLoop && !prefersReducedMotion

  const carouselOpts = useMemo(
    () => ({
      align: 'start' as const,
      loop: enableLoop,
      containScroll: false as const,
      duration: prefersReducedMotion ? 0 : SCROLL_DURATION,
      dragFree: false,
      skipSnaps: false,
    }),
    [enableLoop, prefersReducedMotion],
  )

  const bumpEpoch = useCallback(() => setProgressEpoch((e) => e + 1), [])

  const goToSlide = useCallback(
    (index: number) => {
      if (!api) return
      setFillRatio(0)
      api.scrollTo(index)
      bumpEpoch()
    },
    [api, bumpEpoch],
  )

  // Sincroniza o slide ativo.
  useEffect(() => {
    if (!api) return
    const sync = () => {
      const index = api.selectedScrollSnap()
      setCurrent((prev) => {
        if (prev !== index) {
          setFillRatio(0)
          setProgressEpoch((e) => e + 1)
        }
        return index
      })
    }
    sync()
    api.on('select', sync)
    api.on('reInit', sync)
    return () => {
      api.off('select', sync)
      api.off('reInit', sync)
    }
  }, [api])

  // Autoplay manual (sem dependência externa); respeita reduced-motion.
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    if (!api || !autoplayEnabled) return
    intervalRef.current = setInterval(() => api.scrollNext(), AUTOPLAY_MS)
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [api, autoplayEnabled, progressEpoch])

  // Barra de progresso do bullet ativo (desligada em reduced-motion).
  useEffect(() => {
    if (!autoplayEnabled) {
      setFillRatio(0)
      return
    }
    setFillRatio(0)
    let raf = 0
    let start = 0
    const tick = (now: number) => {
      if (!start) start = now
      const ratio = Math.min(1, (now - start) / AUTOPLAY_MS)
      setFillRatio(ratio)
      if (ratio < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [current, progressEpoch, autoplayEnabled])

  if (slideCount === 0) return null

  return (
    <div className={cn('flex flex-col gap-3', visibleBelow === 'lg' ? 'lg:hidden' : 'md:hidden')}>
      {/* Os títulos dos cards são h3: sem este h2 a página pulava de h1 para h3 (heading-order). */}
      <h2 className="sr-only">{t('topStoriesHeading')}</h2>
      <Card className="gap-0 overflow-hidden rounded-none border-0 bg-transparent p-0 py-0 shadow-none">
        <div
          className="relative aspect-[16/10] min-h-[14rem] w-full max-h-[28rem] touch-pan-y sm:aspect-[16/9] md:max-h-[32rem]"
          aria-label={t('carouselHighlights')}
        >
          <Carousel
            setApi={setApi}
            opts={carouselOpts}
            className="h-full w-full cursor-grab active:cursor-grabbing"
          >
            <CarouselContent className="ml-0 h-full">
              {slides.map((item, index) => (
                <CarouselItem key={item.id} className="h-full basis-full pl-0">
                  <article className="group relative size-full">
                    <LocaleLink
                      {...externalArticleLink}
                      href={item.href}
                      className="relative block size-full overflow-hidden rounded-[var(--post-thumb-radius,0px)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                      draggable={false}
                    >
                      <HomeCardImage
                        src={item.img}
                        srcSizes={item.imgSizes}
                        alt={item.alt}
                        className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                        sizes="100vw"
                        priority={index === 0}
                        draggable={false}
                      />
                      <div
                        className="absolute inset-0 bg-linear-to-t from-black/85 via-black/25 to-transparent"
                        aria-hidden
                      />
                      <div className="absolute inset-x-0 bottom-0 z-10 px-4 pt-12 pb-5">
                        {item.featuredBadge ? (
                          // R7 — destaque ativo: o carimbo SUBSTITUI a categoria. Sobre a foto, o
                          // fundo sólido do selo é o que garante contraste — por isso ele não usa o
                          // cinza do rótulo.
                          <div className="mb-1.5 flex min-w-0 items-center gap-2">
                            <SeloDestaque badge={item.featuredBadge} />
                          </div>
                        ) : item.category ? (
                          <p className="mb-1.5 text-[10px] font-bold tracking-wide text-white/80 uppercase">
                            {item.category}
                          </p>
                        ) : null}
                        <h3
                          className={cn(
                            HOME_BI_NEWS_TITLE_LINK_ON_DARK,
                            'text-xl leading-snug font-semibold tracking-tight text-white drop-shadow-md sm:text-2xl',
                          )}
                        >
                          {item.title}
                        </h3>
                        <p className="mt-2 text-[11px] font-semibold tracking-wide text-white/85 uppercase">
                          {item.read}
                        </p>
                      </div>
                    </LocaleLink>
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </Card>

      {slideCount > 1 ? (
        <div className="flex w-full items-center" role="tablist" aria-label={t('carouselNav')}>
          {slides.map((item, index) => {
            const isActive = index === current
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={t('slideLabel', {
                  current: index + 1,
                  total: slideCount,
                  title: item.title,
                })}
                // Área de toque de 24px (target-size); a barrinha visível continua com 8px.
                className="group flex h-6 min-w-6 shrink-0 items-center justify-center focus-visible:outline-none"
                onClick={() => goToSlide(index)}
              >
                <span
                  aria-hidden
                  className={cn(
                    'relative overflow-hidden rounded-full transition-none group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2',
                    isActive
                      ? `${ACTIVE_BULLET} bg-foreground/15`
                      : `${INACTIVE_BULLET} bg-muted-foreground/35`,
                  )}
                >
                  {isActive ? (
                    <span
                      className="absolute inset-0 origin-left rounded-full bg-foreground will-change-transform"
                      style={{ transform: `scaleX(${fillRatio})` }}
                      aria-hidden
                    />
                  ) : null}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
